import { t, type I18nLocale } from '@ardoq/i18n';

export type CollapsingRuleErrorCode =
  | { code: 'overlappingWithItself' }
  | { code: 'subpathOfExistingRule' }
  | { code: 'superpathOfExistingPath' }
  | { code: 'partiallyOverlappingPath' }
  | { code: 'dividingGraph' }
  | { code: 'pathMustHideAtLeastOneComponent' }
  | { code: 'collapsingPathHidesStartNode'; componentTypeName: string }
  | { code: 'unexpected' };

export const CollapsingRuleErrorMessage = {
  overlappingWithItself: { code: 'overlappingWithItself' } as const,
  subpathOfExistingRule: { code: 'subpathOfExistingRule' } as const,
  superpathOfExistingPath: { code: 'superpathOfExistingPath' } as const,
  partiallyOverlappingPath: { code: 'partiallyOverlappingPath' } as const,
  dividingGraph: { code: 'dividingGraph' } as const,
  pathMustHideAtLeastOneComponent: {
    code: 'pathMustHideAtLeastOneComponent',
  } as const,
  collapsingPathHidesStartNode: (
    componentTypeName: string
  ): CollapsingRuleErrorCode => ({
    code: 'collapsingPathHidesStartNode',
    componentTypeName,
  }),
  unexpected: { code: 'unexpected' } as const,
};

export const translateCollapsingRuleError = (
  error: CollapsingRuleErrorCode,
  i18nLocale: I18nLocale
): string => {
  switch (error.code) {
    case 'overlappingWithItself':
      return t(
        i18nLocale,
        'viewpointBuilder:collapsingRuleError.overlappingWithItself',
        'The selected path contains repeated component and reference type connections that would cause multiple conflicting matches. Please select a path with unique components.'
      );
    case 'subpathOfExistingRule':
      return t(
        i18nLocale,
        'viewpointBuilder:collapsingRuleError.subpathOfExistingRule',
        'The selected path is already in an existing longer collapsed path. Please edit the existing path or select a different path.'
      );
    case 'superpathOfExistingPath':
      return t(
        i18nLocale,
        'viewpointBuilder:collapsingRuleError.superpathOfExistingPath',
        'The selected path contains an existing shorter collapsed path. Please edit the existing path or select a different path.'
      );
    case 'partiallyOverlappingPath':
      return t(
        i18nLocale,
        'viewpointBuilder:collapsingRuleError.partiallyOverlappingPath',
        'The selected path overlaps with an existing collapsed path. Please edit the existing path or select a different path.'
      );
    case 'dividingGraph':
      return t(
        i18nLocale,
        'viewpointBuilder:collapsingRuleError.dividingGraph',
        'The selected path disconnects some components from the context component. Please select a path that keeps the connections to your context component.'
      );
    case 'pathMustHideAtLeastOneComponent':
      return t(
        i18nLocale,
        'viewpointBuilder:collapsingRuleError.pathMustHideAtLeastOneComponent',
        'The selected path must have at least 1 component type between the start and end of the path'
      );
    case 'collapsingPathHidesStartNode':
      return t(
        i18nLocale,
        'viewpointBuilder:collapsingRuleError.collapsingPathHidesStartNode',
        { componentTypeName: error.componentTypeName },
        "Please select a path that doesn't hide the {{componentTypeName}} context component"
      );
    case 'unexpected':
      return t(
        i18nLocale,
        'viewpointBuilder:collapsePaths.errorMessage.unexpected',
        "Unexpected error. Please try again or contact Ardoq's support"
      );
    default:
      error satisfies never;
      return '';
  }
};
